import React from "react";
import {StyleSheet, TextInputProps} from "react-native";
import MaterialIcons from "@expo/vector-icons/MaterialIcons";
import Btn from "../../components/Btn";
import CardEntryFields, {CardEntryFieldsTheme} from "../../components/CardEntryFields";
import CodeVerificationInput from "../../components/CodeVerificationInput";
import Div from "../../lib/Div";
import P from "../../lib/P";
import {formatCurrency} from "../../utils";
import {PaymentType, PayoutVerificationResponse} from "../../types";
import type {CardTokenizationStatus} from "../../hooks/useCardTokenization";
import {colors, layout} from "./theme";
import {CardDestinationView, PayoutMethodView} from "./types";
import {
  CaptionInfo, DestinationRow, EtaCard, FlowSummary, GhostButton, InfoCard, MethodRow, PrimaryButton, StepStack
} from "./WithdrawFlowPrimitives";

type MethodStepProps = {
  amountValue: number, methodRows: PayoutMethodView[], methodType?: PaymentType, isLoadingPayoffMethods: boolean,
  payoffMethodsError: string, canContinue: boolean, onRetry: () => void,
  onSelectMethod: (methodType: PaymentType) => void, onContinue: () => void
};

type DestinationStepProps = {
  methodType: PaymentType, cardDestinations: CardDestinationView[], destinationId: string, isLoadingSavedCards: boolean,
  hasLoadedSavedCards: boolean, savedCardsError: string, canAddCard: boolean, canContinue: boolean,
  hasAlternativeMethod: boolean, onRetry: () => void, onSelectDestination: (destinationId: string) => void,
  onAddCard: () => void, onChooseAnotherMethod: () => void, onBackToWallet: () => void, onContinue: () => void
};

type CardEntryStepProps = {
  accountHolderName: string, cardNumberInputProps: TextInputProps, expiryInputProps: TextInputProps,
  cvcInputProps: TextInputProps, formError: string, isSavingCard: boolean, requiresSavedCardsCheck: boolean,
  tokenizationStatus: CardTokenizationStatus, tokenizationError: string, onSubmit: () => void, onCancel: () => void,
  onCheckSavedCards: () => void, onRetryTokenization: () => void
};

type FinixCardEntryStepProps = {
  mountElementId: string, status: 'idle' | 'loading' | 'ready' | 'error', errorMessage: string, formError: string,
  isSaving: boolean, onSubmit: () => void, onCancel: () => void, onRetry: () => void
};

type PaysafeCardEntryStepProps = {
  fieldHostIds: { cardNumber: string, expiryDate: string, cvv: string },
  status: 'idle' | 'loading' | 'ready' | 'error', errorMessage: string, formError: string, isSaving: boolean,
  onSubmit: () => void, onCancel: () => void, onRetry: () => void
};

type ReviewStepProps = {
  amountValue: number, selectedMethod: PayoutMethodView, selectedDestinationSummary: string, confirmationEmail: string,
  isSubmittingWithdrawal: boolean, submitError?: string, submitLabel?: string, onSubmit: () => void,
  onCancel: () => void
};

type OtpStepProps = {
  amountValue: number, verification?: PayoutVerificationResponse, otpCode: string, formError: string,
  isSubmittingWithdrawal: boolean, isStartingVerification: boolean, isResendingVerification: boolean,
  verificationRestartRequired: boolean, resendAvailableInSeconds: number, onChangeOtpCode: (value: string) => void,
  onSubmit: () => void, onResend: () => void, onRequestNewCode: () => void
};

export const WithdrawMethodStep = (props: MethodStepProps) => {
  const {amountValue, methodRows, methodType, isLoadingPayoffMethods, payoffMethodsError, canContinue} = props;

  return (
    <StepStack>
      <Div>
        <P fontFamily="DavisSans-Medium" color={colors.textSecondary} fs={11} lh={14.3} tt="uppercase" mb={4}>
          Withdrawing
        </P>
        <P fontFamily="DavisSans-Medium" fs={34} lh={35.7} color={colors.accentText}>{formatCurrency(amountValue)}</P>
      </Div>

      {isLoadingPayoffMethods ? (
        <P fontFamily="DavisSans-Regular" fs={14} lh={20.3} color={colors.textSecondary}>
          Loading payout methods...
        </P>
      ) : payoffMethodsError ? (
        <Div gap={layout.stackTight}>
          <P fontFamily="DavisSans-Regular" fs={14} lh={20.3} color={colors.statusWarn}>
            {payoffMethodsError}
          </P>
          <GhostButton label="Retry" onPress={props.onRetry}/>
        </Div>
      ) : (
        <Div gap={layout.methodGap}>
          {methodRows.map(method => (
            <MethodRow
              key={method.type} icon={method.icon} selected={method.available && methodType === method.type}
              title={method.title} subtitle={method.subtitle} eta={method.eta} etaWarn={method.etaWarn}
              feeLabel={method.feeLabel} netAmountLabel={method.netAmountLabel} recommended={method.recommended}
              disabled={!method.available} showAmounts={method.showAmounts}
              onPress={() => props.onSelectMethod(method.type)}
            />
          ))}
        </Div>
      )}

      <CaptionInfo text="Each rail shows the arrival date, fee, and the net amount you'll receive."/>

      <PrimaryButton
        label="Continue" icon="arrow-right" onPress={props.onContinue}
        disabled={isLoadingPayoffMethods || !!payoffMethodsError || !canContinue}
      />
    </StepStack>
  );
};

export const WithdrawDestinationStep = (props: DestinationStepProps) => {
  const {
    methodType, cardDestinations, destinationId, isLoadingSavedCards, hasLoadedSavedCards, savedCardsError, canAddCard,
    canContinue
  } = props;
  const isAwaitingSavedCards = !hasLoadedSavedCards || isLoadingSavedCards;
  const isCardSetupUnavailable = methodType === 'CARD' && !canAddCard && hasLoadedSavedCards && !savedCardsError && !cardDestinations.length;

  return (
    <StepStack>
      <P fontFamily="DavisSans-Medium" color={colors.textSecondary} fs={11} lh={14.3} tt="uppercase">
        {methodType === 'CARD' ? 'Your Saved Cards' : 'Your Bank Account'}
      </P>

      {methodType === 'CARD' ? (
        <>
          {isAwaitingSavedCards ? (
            <P fontFamily="DavisSans-Regular" fs={14} lh={20.3} color={colors.textSecondary}>Loading saved cards...</P>
          ) : savedCardsError ? (
            <Div gap={layout.stackTight}>
              <P fontFamily="DavisSans-Regular" fs={14} lh={20.3} color={colors.statusWarn}>{savedCardsError}</P>
              <GhostButton label="Retry" onPress={props.onRetry}/>
            </Div>
          ) : (
            <Div gap={layout.methodGap}>
              {cardDestinations.length ? (
                <>
                  {cardDestinations.map(destination => (
                    <DestinationRow
                      key={destination.id} icon={destination.icon} selected={destinationId === destination.id}
                      title={destination.title} subtitle={destination.subtitle}
                      onPress={() => props.onSelectDestination(destination.id)}
                    />
                  ))}

                  {canAddCard && (
                    <DestinationRow
                      icon="plus" title="Add new card" subtitle="Save another card for card payouts"
                      variant="add" onPress={props.onAddCard}
                    />
                  )}
                </>
              ) : canAddCard ? (
                <>
                  <InfoCard
                    icon="credit-card"
                    text="No saved payout cards yet. Add a card to continue with an instant card payout."
                  />

                  <DestinationRow
                    icon="plus" title="Add card" subtitle="Save a card without creating a deposit"
                    variant="add" onPress={props.onAddCard}
                  />
                </>
              ) : (
                <>
                  <InfoCard
                    icon="credit-card"
                    text="Card payout is not available on this device until you add a card in the Evenplay web app."
                  />

                  <Div gap={layout.methodGap}>
                    <PrimaryButton
                      label={props.hasAlternativeMethod ? "Choose another method" : "Back to Wallet"}
                      onPress={props.hasAlternativeMethod ? props.onChooseAnotherMethod : props.onBackToWallet}
                    />
                    <GhostButton label="Refresh cards" onPress={props.onRetry}/>
                  </Div>
                </>
              )}
            </Div>
          )}

          {canAddCard ? (
            <InfoCard
              icon="info-circle"
              text="Cards are tokenized securely. Saving a card here does not create a deposit."
            />
          ) : !!cardDestinations.length && (
            <InfoCard icon="info-circle" text="New cards can only be added in the Evenplay web app."/>
          )}
        </>
      ) : (
        <>
          <DestinationRow
            icon="university" title="Bank account destination" subtitle="Bank account linking unavailable"
            disabled onPress={() => undefined}
          />

          <InfoCard
            icon="info-circle"
            text="Saved cards only apply to instant card payouts. Bank account selection is blocked until PayGate exposes a destination API for ACH."
          />
        </>
      )}

      {!isCardSetupUnavailable && (
        <PrimaryButton label="Continue" icon="arrow-right" onPress={props.onContinue} disabled={!canContinue}/>
      )}
    </StepStack>
  );
};

export const WithdrawCardEntryStep = (props: CardEntryStepProps) => {
  const {
    accountHolderName, cardNumberInputProps, expiryInputProps, cvcInputProps, formError, isSavingCard,
    requiresSavedCardsCheck, tokenizationStatus, tokenizationError
  } = props;
  const isPreparingTokenization = tokenizationStatus === 'loading';
  const isTokenizationReady = tokenizationStatus === 'ready';

  return (
    <StepStack>
      <Div gap={layout.stackTight}>
        <P fontFamily="DavisSans-Medium" color={colors.textSecondary} fs={11} lh={14.3} tt="uppercase">
          Payout Card
        </P>
        <P fontFamily="DavisSans-Regular" fs={13} lh={18.2} color={colors.textSecondary}>
          Add a payout card without making a deposit.
        </P>
      </Div>

      <Div bg={colors.bgElev1} rounded={14} p={16} borderWidth={1} borderColor={colors.surfaceBorder} gap={14}>
        <CardEntryFields
          accountHolderName={accountHolderName} cardNumberInputProps={cardNumberInputProps}
          expiryInputProps={expiryInputProps} cvcInputProps={cvcInputProps} disabled={isSavingCard}
          theme={cardEntryFieldsTheme}
        />

        {isPreparingTokenization && (
          <P fontFamily="DavisSans-Regular" fs={13} lh={18.2} color={colors.textSecondary}>
            Preparing secure card entry...
          </P>
        )}

        {tokenizationStatus === 'error' && (
          <Div gap={layout.stackTight}>
            <P fontFamily="DavisSans-Regular" fs={13} lh={18.2} color={colors.statusWarn}>
              {tokenizationError}
            </P>
            <GhostButton label="Retry secure connection" onPress={props.onRetryTokenization}/>
          </Div>
        )}

        {!!formError && formError !== tokenizationError && (
          <P fontFamily="DavisSans-Regular" fs={13} lh={18.2} color={colors.statusWarn}>{formError}</P>
        )}

        {requiresSavedCardsCheck && <GhostButton label="Check saved cards" onPress={props.onCheckSavedCards}/>}
      </Div>

      <InfoCard icon="lock" text="Your card is tokenized by PayGate. Evenplay does not store the raw card number."/>

      <Div gap={layout.methodGap}>
        <PrimaryButton
          label={isSavingCard ? 'Saving...' : isPreparingTokenization ? 'Preparing...' : 'Save Card'}
          onPress={props.onSubmit} disabled={isSavingCard || requiresSavedCardsCheck || !isTokenizationReady}
        />
        <GhostButton label="Cancel" onPress={props.onCancel}/>
      </Div>
    </StepStack>
  );
};

/**
 * Finix hosted-field card entry (SMG-4557).
 *
 * <p>There are no card inputs here on purpose. Finix tokenizes from inside its own cross-origin
 * iframe, so it must own the fields — this step renders the empty host element Finix mounts into
 * and nothing else. The PAN never enters our origin, which is the whole reason for the provider.</p>
 *
 * <p>{@code nativeID} is the DOM {@code id} on web, and Finix resolves the host by id.</p>
 */
export const WithdrawFinixCardEntryStep = (props: FinixCardEntryStepProps) => {
  const {mountElementId, status, errorMessage, formError, isSaving} = props;
  const isPreparing = status === 'loading' || status === 'idle';
  const isReady = status === 'ready';

  return (
    <StepStack>
      <Div gap={layout.stackTight}>
        <P fontFamily="DavisSans-Medium" color={colors.textSecondary} fs={11} lh={14.3} tt="uppercase">
          Payout Card
        </P>
        <P fontFamily="DavisSans-Regular" fs={13} lh={18.2} color={colors.textSecondary}>
          Add a payout card without making a deposit.
        </P>
      </Div>

      <Div bg={colors.bgElev1} rounded={14} p={16} borderWidth={1} borderColor={colors.surfaceBorder} gap={14}>
        <Div nativeID={mountElementId} style={isReady ? cardEntryStyles.finixFormHost : undefined}/>

        {isPreparing && (
          <P fontFamily="DavisSans-Regular" fs={13} lh={18.2} color={colors.textSecondary}>
            Preparing secure card entry...
          </P>
        )}

        {status === 'error' && (
          <Div gap={layout.stackTight}>
            <P fontFamily="DavisSans-Regular" fs={13} lh={18.2} color={colors.statusWarn}>{errorMessage}</P>
            <GhostButton label="Retry secure connection" onPress={props.onRetry}/>
          </Div>
        )}

        {!!formError && (
          <P fontFamily="DavisSans-Regular" fs={13} lh={18.2} color={colors.statusWarn}>{formError}</P>
        )}
      </Div>

      <InfoCard icon="lock" text="Your card is tokenized by Finix. Evenplay does not store the raw card number."/>

      <Div gap={layout.methodGap}>
        <PrimaryButton
          label={isSaving ? 'Saving...' : isPreparing ? 'Preparing...' : 'Save Card'}
          onPress={props.onSubmit} disabled={isSaving || !isReady}
        />
        <GhostButton label="Cancel" onPress={props.onCancel}/>
      </Div>
    </StepStack>
  );
};

/**
 * Paysafe hosted-field card entry (SMG-4057). Same contract as the Finix step above: no card inputs
 * of our own, only the empty hosts Paysafe mounts its card number / expiry / CVV iframes into
 * ({@code nativeID} is the DOM {@code id} on web). The PAN never enters our origin.
 *
 * <p>Customer-invisible (PS-17): nothing rendered here names the processor.</p>
 *
 * <p>Unlike Finix, Paysafe renders bare inputs, so the labels and input boxes are ours. They stay
 * rendered while preparing (the hosts must be in the DOM to mount into) and are hidden — not removed
 * — on a preparation error, so a retry can mount into the same hosts.</p>
 */
export const WithdrawPaysafeCardEntryStep = (props: PaysafeCardEntryStepProps) => {
  const {fieldHostIds, status, errorMessage, formError, isSaving} = props;
  const isPreparing = status === 'loading' || status === 'idle';
  const isReady = status === 'ready';

  return (
    <StepStack>
      <Div gap={layout.stackTight}>
        <P fontFamily="DavisSans-Medium" color={colors.textSecondary} fs={11} lh={14.3} tt="uppercase">
          Payout Card
        </P>
        <P fontFamily="DavisSans-Regular" fs={13} lh={18.2} color={colors.textSecondary}>
          Add a payout card without making a deposit.
        </P>
      </Div>

      <Div bg={colors.bgElev1} rounded={14} p={16} borderWidth={1} borderColor={colors.surfaceBorder} gap={14}>
        <Div gap={14} style={status === 'error' ? cardEntryStyles.hiddenHostedFields : undefined}>
          <Div>
            <P {...cardEntryFieldsTheme.labelProps}>Card Number</P>
            <Div nativeID={fieldHostIds.cardNumber} style={cardEntryStyles.hostedField}/>
          </Div>

          <Div row gap={12}>
            <Div flex={1}>
              <P {...cardEntryFieldsTheme.labelProps}>Expiry Date</P>
              <Div nativeID={fieldHostIds.expiryDate} style={cardEntryStyles.hostedField}/>
            </Div>

            <Div flex={1}>
              <P {...cardEntryFieldsTheme.labelProps}>CVC</P>
              <Div nativeID={fieldHostIds.cvv} style={cardEntryStyles.hostedField}/>
            </Div>
          </Div>
        </Div>

        {isPreparing && (
          <P fontFamily="DavisSans-Regular" fs={13} lh={18.2} color={colors.textSecondary}>
            Preparing secure card entry...
          </P>
        )}

        {status === 'error' && (
          <Div gap={layout.stackTight}>
            <P fontFamily="DavisSans-Regular" fs={13} lh={18.2} color={colors.statusWarn}>{errorMessage}</P>
            <GhostButton label="Retry secure connection" onPress={props.onRetry}/>
          </Div>
        )}

        {!!formError && (
          <P fontFamily="DavisSans-Regular" fs={13} lh={18.2} color={colors.statusWarn}>{formError}</P>
        )}
      </Div>

      <InfoCard
        icon="lock" text="Your card is secured by our payment processor. Evenplay does not store the raw card number."
      />

      <Div gap={layout.methodGap}>
        <PrimaryButton
          label={isSaving ? 'Saving...' : isPreparing ? 'Preparing...' : 'Save Card'}
          onPress={props.onSubmit} disabled={isSaving || !isReady}
        />
        <GhostButton label="Cancel" onPress={props.onCancel}/>
      </Div>
    </StepStack>
  );
};

export const WithdrawReviewStep = (props: ReviewStepProps) => {
  const {
    amountValue, selectedMethod, selectedDestinationSummary, confirmationEmail, isSubmittingWithdrawal, submitError,
    submitLabel = 'Submitting...'
  } = props;

  return (
    <StepStack>
      <EtaCard title={selectedMethod.reviewEta} subtitle={selectedMethod.reviewSubtitle}/>

      <FlowSummary
        lines={[
          {label: 'From', value: 'Evenplay Wallet'}, {label: 'To', value: selectedDestinationSummary},
          {label: 'Method', value: selectedMethod.reviewTitle}, {label: 'Amount', value: formatCurrency(amountValue)},
          {label: 'Network fee', value: selectedMethod.feeLabel}
        ]}
        total={{label: "You'll receive", value: selectedMethod.netAmountLabel}}
      />

      <P fontFamily="DavisSans-Regular" fs={13} lh={18.2} color={colors.textSecondary}>
        By tapping Confirm, you agree to the{' '}
        <P fontFamily="DavisSans-Regular" fs={13} lh={18.2} color={colors.accentText}>Withdrawal Terms</P>
        {confirmationEmail ? (
          <>
            . We'll send a confirmation to{' '}
            <P fontFamily="DavisSans-Regular" fs={13} lh={18.2} color={colors.textPrimary}>
              {confirmationEmail}
            </P>
            .
          </>
        ) : '. We\'ll send a confirmation to your email.'}
      </P>

      {!!submitError && (
        <InfoCard icon="exclamation-triangle" text={submitError}/>
      )}

      <Div gap={layout.methodGap}>
        <PrimaryButton
          label={isSubmittingWithdrawal ? submitLabel : 'Confirm Withdrawal'} onPress={props.onSubmit}
          disabled={isSubmittingWithdrawal}
        />
        <GhostButton label="Cancel" onPress={props.onCancel}/>
      </Div>
    </StepStack>
  );
};

export const WithdrawOtpStep = (props: OtpStepProps) => {
  const {
    amountValue, verification, otpCode, formError, isSubmittingWithdrawal, isResendingVerification,
    isStartingVerification, verificationRestartRequired, resendAvailableInSeconds
  } = props;
  const maskedDestination = verification?.maskedDestination || 'your phone or email';
  const deliveryMethod = verification?.deliveryMethod || 'OTP';
  const normalizedDeliveryMethod = deliveryMethod.toLowerCase();
  const deliveryVerb = normalizedDeliveryMethod.includes('email') ? 'emailed' : normalizedDeliveryMethod.includes('sms') || normalizedDeliveryMethod.includes('text') ? 'texted' : 'sent';
  const resendLabel = resendAvailableInSeconds > 0 ? `Resend code (${resendAvailableInSeconds}s)` : isResendingVerification ? 'Resending...' : 'Resend code';

  return (
    <StepStack>
      <Div alignItems="center" pt={8}>
        <P fontFamily="DavisSans-Regular" fs={14} lh={20.3} ta="center" color={colors.textSecondary} maxW={330}>
          {verificationRestartRequired ? (
            'This verification can no longer be used. Request a new code to continue your withdrawal.'
          ) : (
            <>
              Enter the 6-digit code we {deliveryVerb} to {maskedDestination} to confirm your{' '}
              <P fontFamily="DavisSans-Medium" fs={14} lh={20.3} color={colors.textPrimary}>
                {formatCurrency(amountValue)}
              </P>
              {' '}withdrawal
            </>
          )}
        </P>
      </Div>

      {verificationRestartRequired ? (
        <Div gap={layout.stackTight}>
          {!!formError && (
            <P fontFamily="DavisSans-Regular" fs={13} lh={18.2} ta="center" color={colors.statusWarn}>
              {formError}
            </P>
          )}
          <PrimaryButton
            label={isStartingVerification ? 'Sending Code...' : 'Request a new code'}
            onPress={props.onRequestNewCode} disabled={isStartingVerification || isSubmittingWithdrawal}
          />
        </Div>
      ) : (
        <>
          <Div gap={14}>
            <CodeVerificationInput
              value={otpCode} onChange={props.onChangeOtpCode}
              disabled={isSubmittingWithdrawal || isResendingVerification} secureTextEntry
              containerStyle={cardEntryStyles.otpContainer} itemStyle={cardEntryStyles.otpItem}
              filledItemStyle={cardEntryStyles.otpFilledItem} textStyle={cardEntryStyles.otpText}
            />

            {!!formError && (
              <P fontFamily="DavisSans-Regular" fs={13} lh={18.2} ta="center" color={colors.statusWarn}>{formError}</P>
            )}

            <Btn disabled={isSubmittingWithdrawal || isResendingVerification || resendAvailableInSeconds > 0}
                 onPress={resendAvailableInSeconds > 0 || isResendingVerification || isSubmittingWithdrawal ? undefined : props.onResend}
                 alignSelf="center" rounded={999} px={12} py={8}
                 opacity={isSubmittingWithdrawal || isResendingVerification || resendAvailableInSeconds > 0 ? 0.45 : undefined}
                 accessibilityRole="button"
                 accessibilityState={{disabled: isSubmittingWithdrawal || isResendingVerification || resendAvailableInSeconds > 0}}>
              <P fontFamily="DavisSans-Medium" fs={14} lh={21} ta="center" color={colors.accentText}>{resendLabel}</P>
            </Btn>
          </Div>

          <PrimaryButton
            label={isSubmittingWithdrawal ? 'Submitting...' : 'Confirm'} onPress={props.onSubmit}
            disabled={isSubmittingWithdrawal || isResendingVerification || otpCode.length !== 6}
          />
        </>
      )}

      <Div row alignItems="center" justifyContent="center" gap={6}>
        <MaterialIcons name="lock" size={16} color={colors.textMuted}/>
        <P fontFamily="DavisSans-Regular" fs={11} lh={16.5} color={colors.textMuted}>
          Secured by Evenplay Wallet Protocol
        </P>
      </Div>
    </StepStack>
  );
};

const cardEntryStyles = StyleSheet.create({
  input: {
    height: 52, borderRadius: 12, backgroundColor: colors.bgInset, borderWidth: 1, borderColor: colors.hairlineStrong,
    color: colors.textPrimary, paddingHorizontal: 14, fontFamily: 'DavisSans-Regular', fontSize: 14
  },
  inputWithIconText: {paddingLeft: 44},
  otpContainer: {marginBottom: 0},
  otpItem: {
    maxWidth: '100%', height: 66, marginHorizontal: 4, borderRadius: 12, backgroundColor: colors.bgInset,
    borderColor: colors.hairlineStrong
  },
  otpFilledItem: {borderColor: colors.accent},
  otpText: {fontFamily: 'DavisSans-Regular', fontSize: 24, lineHeight: 30, color: colors.textPrimary},
  inputIcon: {position: 'absolute', left: 14, top: 16, zIndex: 1},
  // Reserves room for Finix's iframe once it has mounted. Zero-height before that, so the
  // "Preparing secure card entry..." state does not leave a gap.
  finixFormHost: {minHeight: 190},
  // The box around one Paysafe iframe; Paysafe styles the input inside it (see usePaysafeCardEntry).
  hostedField: {
    height: 52, borderRadius: 12, backgroundColor: colors.bgInset, borderWidth: 1, borderColor: colors.hairlineStrong,
    paddingHorizontal: 14, justifyContent: 'center', overflow: 'hidden'
  },
  hiddenHostedFields: {display: 'none'}
});

const cardEntryFieldsTheme: CardEntryFieldsTheme = {
  labelProps: {
    fontFamily: 'DavisSans-Medium', fs: 11, lh: 16.5, tt: 'uppercase', color: colors.textSecondary, mb: 6
  },
  inputStyle: cardEntryStyles.input, inputWithIconTextStyle: cardEntryStyles.inputWithIconText,
  inputIconStyle: cardEntryStyles.inputIcon, placeholderTextColor: colors.textMuted, iconColor: colors.textMuted
};
